import React, { useState } from 'react';
import { AppSettings } from '@/types/settings';
import { clearAllHistory } from '@/lib/storage/db';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/Toast';
import {
  Settings,
  Shield,
  Trash2,
  Smartphone,
  CheckCircle2,
  HardDrive
} from 'lucide-react';

interface SettingsManagerProps {
  settings: AppSettings;
  onUpdateSettings: (settings: AppSettings) => void;
  onInstallPWA?: () => void;
  canInstallPWA?: boolean;
}

export const SettingsManager: React.FC<SettingsManagerProps> = ({
  settings,
  onUpdateSettings,
  onInstallPWA,
  canInstallPWA
}) => {
  const { showToast } = useToast();
  const [isClearDataOpen, setIsClearDataOpen] = useState(false);

  const handleClearData = async () => {
    await clearAllHistory();
    localStorage.clear();
    showToast('info', 'All Local Data Cleared', 'Reloading with default settings...');
    setTimeout(() => window.location.reload(), 800);
  };

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="p-6 rounded-2xl bg-card border border-border">
        <h3 className="text-[21px] font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Settings className="h-5 w-5 text-muted-foreground" />
          Settings & Local Preferences
        </h3>
        <p className="text-[14px] text-muted-foreground mt-0.5">
          Configure default export options, accessibility, and storage
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* PWA & Installation */}
        <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
          <h4 className="text-[17px] font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-primary" />
            Progressive Web App (PWA)
          </h4>
          <p className="text-[14px] text-muted-foreground leading-relaxed">
            Install QR Studio as a standalone desktop or mobile application for instant offline access.
          </p>

          {canInstallPWA && onInstallPWA ? (
            <Button variant="primary" size="md" onClick={onInstallPWA} leftIcon={<Smartphone className="h-4 w-4" />}>
              Install QR Studio App
            </Button>
          ) : (
            <div className="p-3 rounded-lg bg-success/10 border border-success/20 text-success text-[14px] flex items-center gap-2 font-normal">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              PWA Offline Cache Active & Ready
            </div>
          )}
        </div>

        {/* Default Export Defaults */}
        <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
          <h4 className="text-[17px] font-semibold text-foreground tracking-tight flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-primary" />
            Default QR Generation Defaults
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Default Format"
              value={settings.defaultExportFormat}
              onChange={(e) =>
                onUpdateSettings({ ...settings, defaultExportFormat: e.target.value as any })
              }
              options={[
                { value: 'png', label: 'PNG (Raster)' },
                { value: 'svg', label: 'SVG (Vector)' },
                { value: 'webp', label: 'WebP' }
              ]}
            />

            <Select
              label="Default Size"
              value={String(settings.defaultExportSize)}
              onChange={(e) =>
                onUpdateSettings({ ...settings, defaultExportSize: Number(e.target.value) as any })
              }
              options={[
                { value: '512', label: '512px (Standard)' },
                { value: '1024', label: '1024px (HD Print)' },
                { value: '2048', label: '2048px (2K Ultra)' }
              ]}
            />
          </div>
        </div>

        {/* Privacy & Storage */}
        <div className="p-6 rounded-2xl bg-card border border-border space-y-4 md:col-span-2">
          <h4 className="text-[17px] font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Shield className="h-4 w-4 text-success" />
            Privacy & Data Sovereignty
          </h4>
          <p className="text-[14px] text-muted-foreground leading-relaxed">
            All operations happen locally in your browser. No telemetry or QR content is ever transmitted.
          </p>

          <Button
            variant="danger"
            size="sm"
            onClick={() => setIsClearDataOpen(true)}
            leftIcon={<Trash2 className="h-4 w-4" />}
          >
            Clear All Stored Data
          </Button>
        </div>
      </div>

      <ConfirmModal
        isOpen={isClearDataOpen}
        onClose={() => setIsClearDataOpen(false)}
        onConfirm={handleClearData}
        title="Clear all local data?"
        description="This clears history, starred favorites, and preferences, then reloads with default settings."
        confirmLabel="Clear Data"
      />
    </div>
  );
};
